<script setup lang="ts">
import type { Product } from '~/types'

const route = useRoute()
const { fetchProductBySlug, fetchRelatedProducts } = useProducts()
const { addItem, isInCart, getQuantity, updateQuantity } = useQuoteCart()

const slug = route.params.slug as string

// Fetch product data
const { data: product, error } = await useAsyncData(
  `product-${slug}`,
  () => fetchProductBySlug(slug)
)

// Handle 404
if (error.value || !product.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Producto no encontrado'
  })
}

// Related products
const relatedProducts = ref<Product[]>([])
onMounted(async () => {
  if (product.value) {
    relatedProducts.value = await fetchRelatedProducts(product.value, 4)
  }
})

// Cart functionality
const quantity = ref(1)
const inCart = computed(() => product.value ? isInCart(product.value.id) : false)
const cartQuantity = computed(() => product.value ? getQuantity(product.value.id) : 0)

const handleAddToCart = () => {
  if (!product.value) return

  addItem({
    productId: product.value.id,
    productSlug: product.value.slug,
    productName: product.value.name,
    productSku: product.value.stock_code,
    packSize: product.value.pack_size,
    unit: product.value.unit,
    imageUrl: product.value.image_url
  }, quantity.value)
  quantity.value = 1
}

const handleUpdateQuantity = (newQty: number) => {
  if (!product.value) return
  updateQuantity(product.value.id, newQty)
}

// Format pack size display
const packSizeDisplay = computed(() => {
  if (!product.value?.pack_size) return null
  return `${product.value.pack_size} ${product.value.unit}`
})

// Check if image is external (from axalta.com)
const isExternalImage = computed(() =>
  product.value?.image_url?.startsWith('http')
)

// SEO
useSeoMeta({
  title: () => `${product.value?.name} - Granados Pinturas Industriales`,
  description: () => `${product.value?.name} - Código: ${product.value?.stock_code}. Solicite cotización de pinturas industriales Axalta.`
})
</script>

<template>
  <div v-if="product">
    <UContainer class="py-8">
      <!-- Breadcrumb -->
      <UBreadcrumb
        :items="[
          { label: 'Catálogo', to: '/' },
          { label: product.category?.name || 'Productos', to: product.category?.slug ? `/?categoria=${product.category.id}` : '/' },
          { label: product.name }
        ]"
        class="mb-6"
      />

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        <!-- Product Image - Enhanced with better empty state -->
        <div class="lg:sticky lg:top-24 h-fit">
          <div class="aspect-square bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-2xl overflow-hidden p-8 shadow-xl border-2 border-gray-200 dark:border-gray-700 image-container">
            <!-- Use regular img for external URLs to avoid Netlify image optimization issues -->
            <img
              v-if="product.image_url && isExternalImage"
              :src="product.image_url"
              :alt="product.name"
              class="w-full h-full object-contain drop-shadow-lg"
              loading="lazy"
              decoding="async"
            />
            <!-- Use NuxtImg for local/internal images -->
            <NuxtImg
              v-else-if="product.image_url"
              :src="product.image_url"
              :alt="product.name"
              class="w-full h-full object-contain drop-shadow-lg"
            />
            <div v-else class="w-full h-full flex items-center justify-center empty-state">
              <div class="text-center">
                <div class="w-32 h-32 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center">
                  <UIcon name="i-heroicons-photo" class="w-20 h-20 text-gray-300 dark:text-gray-600" />
                </div>
                <p class="text-lg font-semibold text-gray-400 dark:text-gray-600">Imagen no disponible</p>
                <p class="text-sm text-gray-400 dark:text-gray-600 mt-1">Este producto no tiene imagen</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Product Info -->
        <div>
          <!-- Badges - Enhanced with better contrast -->
          <div class="flex flex-wrap gap-2.5 mb-6">
            <UBadge
              v-if="product.product_line?.name"
              :label="product.product_line.name"
              color="primary"
              size="lg"
              class="badge-enhanced px-4 py-2 text-sm font-bold shadow-sm"
            />
            <UBadge
              v-if="product.category?.name"
              :label="product.category.name"
              color="neutral"
              variant="outline"
              size="lg"
              class="badge-enhanced px-4 py-2 text-sm font-bold"
            />
            <UBadge
              v-if="product.component_type"
              :label="product.component_type"
              color="neutral"
              variant="subtle"
              size="lg"
              class="badge-enhanced px-4 py-2 text-sm font-bold"
            />
          </div>

          <!-- Title - Enhanced typography -->
          <h1 class="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-4 leading-tight high-contrast">
            {{ product.name }}
          </h1>

          <!-- SKU - Enhanced visibility -->
          <div class="inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-800/50 rounded-xl mb-8 border-2 border-gray-200 dark:border-gray-700 shadow-sm">
            <UIcon name="i-heroicons-hashtag" class="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <span class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Código:</span>
            <span class="font-mono text-lg font-bold text-gray-900 dark:text-white">{{ product.stock_code }}</span>
          </div>

          <!-- Product Details - Enhanced cards -->
          <div class="space-y-4 mb-8">
            <div v-if="packSizeDisplay" class="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800">
              <div class="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <UIcon name="i-heroicons-cube-solid" class="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p class="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-0.5">Presentación</p>
                <p class="text-lg font-bold text-blue-900 dark:text-blue-200">{{ packSizeDisplay }}</p>
              </div>
            </div>

            <div v-if="product.kit_info" class="flex items-start gap-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border-2 border-purple-200 dark:border-purple-800">
              <div class="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mt-0.5">
                <UIcon name="i-heroicons-beaker-solid" class="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div class="flex-1">
                <p class="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-1">Información del Kit</p>
                <p class="text-sm font-semibold text-purple-900 dark:text-purple-200 leading-relaxed">
                  {{ product.kit_info }}
                </p>
              </div>
            </div>

            <div v-if="product.special_conditions" class="flex items-start gap-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border-2 border-amber-300 dark:border-amber-700 shadow-sm">
              <div class="flex-shrink-0 w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center mt-0.5">
                <UIcon name="i-heroicons-exclamation-triangle-solid" class="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div class="flex-1">
                <p class="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1">Condiciones Especiales</p>
                <p class="text-sm font-bold text-amber-900 dark:text-amber-200 leading-relaxed">
                  {{ product.special_conditions }}
                </p>
              </div>
            </div>
          </div>

          <!-- Technical Sheet - Enhanced button -->
          <div v-if="product.technical_sheet_url" class="mb-8">
            <UButton
              :to="product.technical_sheet_url"
              target="_blank"
              variant="outline"
              size="xl"
              icon="i-heroicons-document-arrow-down-solid"
              class="touch-target font-bold shadow-md hover:shadow-lg transition-shadow w-full sm:w-auto"
            >
              Descargar Ficha Técnica
            </UButton>
          </div>

          <!-- Add to Quote - Enhanced CTA section -->
          <UCard class="bg-gradient-to-br from-primary-50 to-red-50 dark:from-primary-900/20 dark:to-red-900/10 border-2 border-primary-200 dark:border-primary-800 shadow-xl">
            <div class="flex flex-col gap-6">
              <div>
                <div class="flex items-center gap-2 mb-2">
                  <UIcon name="i-heroicons-clipboard-document-list-solid" class="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  <h3 class="text-xl font-black text-gray-900 dark:text-white high-contrast">
                    Solicitar Cotización
                  </h3>
                </div>
                <p class="text-sm font-semibold text-gray-600 dark:text-gray-400">
                  Agregue este producto a su lista y reciba una cotización personalizada
                </p>
              </div>

              <!-- Cart actions wrapped in ClientOnly to prevent hydration mismatch -->
              <ClientOnly>
                <!-- Not in cart state -->
                <div v-if="!inCart" class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <div class="flex-shrink-0">
                    <label class="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Cantidad</label>
                    <UInput
                      v-model.number="quantity"
                      type="number"
                      min="1"
                      class="w-28 input-enhanced"
                      size="xl"
                      :ui="{
                        base: 'text-center font-bold text-xl',
                        rounded: 'rounded-xl'
                      }"
                    />
                  </div>
                  <div class="flex-1 flex items-end">
                    <UButton
                      color="primary"
                      size="xl"
                      @click="handleAddToCart"
                      class="w-full btn-primary-enhanced touch-target shadow-lg hover:shadow-xl transition-all font-black text-base"
                    >
                      <UIcon name="i-heroicons-plus-circle-solid" class="w-6 h-6 mr-2" />
                      Agregar a Cotización
                    </UButton>
                  </div>
                </div>

                <!-- In cart state -->
                <div v-else class="flex flex-col gap-4">
                  <div class="flex items-center justify-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-md border-2 border-gray-200 dark:border-gray-700">
                    <UButton
                      color="primary"
                      variant="solid"
                      size="xl"
                      icon="i-heroicons-minus-solid"
                      @click="handleUpdateQuantity(cartQuantity - 1)"
                      class="w-14 h-14 touch-target interactive-scale"
                      square
                    />
                    <div class="min-w-24 text-center px-4">
                      <span class="block text-4xl font-black text-gray-900 dark:text-white quantity-display">{{ cartQuantity }}</span>
                      <span class="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">unidades</span>
                    </div>
                    <UButton
                      color="primary"
                      variant="solid"
                      size="xl"
                      icon="i-heroicons-plus-solid"
                      @click="handleUpdateQuantity(cartQuantity + 1)"
                      class="w-14 h-14 touch-target interactive-scale"
                      square
                    />
                  </div>

                  <div class="flex items-center justify-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border-2 border-green-200 dark:border-green-800">
                    <UIcon name="i-heroicons-check-circle-solid" class="w-6 h-6 text-green-600 dark:text-green-400" />
                    <span class="text-base font-bold text-green-900 dark:text-green-200">
                      Producto agregado a su cotización
                    </span>
                  </div>

                  <UButton
                    to="/cotizacion"
                    color="primary"
                    size="xl"
                    variant="outline"
                    class="w-full btn-primary-enhanced touch-target font-bold text-base"
                  >
                    <UIcon name="i-heroicons-arrow-right-circle-solid" class="w-5 h-5 mr-2" />
                    Ver Mi Cotización
                  </UButton>
                </div>
                <template #fallback>
                  <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                    <div class="flex-shrink-0">
                      <label class="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Cantidad</label>
                      <UInput
                        type="number"
                        min="1"
                        value="1"
                        class="w-28 input-enhanced"
                        size="xl"
                        disabled
                        :ui="{
                          base: 'text-center font-bold text-xl',
                          rounded: 'rounded-xl'
                        }"
                      />
                    </div>
                    <div class="flex-1 flex items-end">
                      <UButton
                        color="primary"
                        size="xl"
                        disabled
                        class="w-full btn-primary-enhanced touch-target shadow-lg font-black text-base"
                      >
                        <UIcon name="i-heroicons-plus-circle-solid" class="w-6 h-6 mr-2" />
                        Agregar a Cotización
                      </UButton>
                    </div>
                  </div>
                </template>
              </ClientOnly>
            </div>
          </UCard>
        </div>
      </div>

      <!-- Related Products - Enhanced section -->
      <div v-if="relatedProducts.length > 0" class="mt-20">
        <div class="divider-enhanced mb-8"></div>
        <div class="text-center mb-10">
          <h2 class="text-3xl md:text-4xl font-black text-gray-900 dark:text-white mb-3 high-contrast">
            Productos Relacionados
          </h2>
          <p class="text-base font-semibold text-gray-500 dark:text-gray-400">
            Otros productos que podrían interesarle
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <CatalogProductCard
            v-for="relatedProduct in relatedProducts"
            :key="relatedProduct.id"
            :product="relatedProduct"
          />
        </div>
      </div>
    </UContainer>
  </div>
</template>
